import { addDays, format } from 'date-fns';

/**
 * Placeholder substitution for note templates.
 *
 * Templates are stored raw (server-side) and only ever expanded here, in the browser, at
 * the moment an engineer inserts one into the note box — so the same template works on any
 * ticket and the engineer always sees (and can edit) the filled-in text before it is sent.
 *
 * Syntax: `{{contact.first_name}}`, with an optional fallback `{{contact.first_name|there}}`
 * used when the ticket has no value for that field.
 */

/** Everything a template can reference. Any field may be missing on a given ticket. */
export interface TemplateContext {
  contactFirstName?: string | null;
  contactLastName?: string | null;
  contactEmail?: string | null;
  companyName?: string | null;
  ticketId?: number | null;
  ticketNumber?: string | null;
  ticketTitle?: string | null;
  myFirstName?: string | null;
  myFullName?: string | null;
}

export interface TemplateVariable {
  token: string;
  label: string;
  /** Shown in the variable menu so engineers can see what it turns into. */
  hint: string;
}

/** The tokens offered in the template editor's "Insert variable" menu. */
export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  { token: 'contact.first_name', label: 'Contact first name', hint: 'Jane' },
  { token: 'contact.last_name',  label: 'Contact last name',  hint: 'Doe' },
  { token: 'contact.full_name',  label: 'Contact full name',  hint: 'Jane Doe' },
  { token: 'contact.email',      label: 'Contact email',      hint: 'jane@acme.com' },
  { token: 'company.name',       label: 'Company name',       hint: 'Acme Pty Ltd' },
  { token: 'ticket.number',      label: 'Ticket number',      hint: 'T20260907.0001' },
  { token: 'ticket.title',       label: 'Ticket title',       hint: 'Outlook not syncing' },
  { token: 'ticket.id',          label: 'Ticket ID',          hint: '12345' },
  { token: 'me.first_name',      label: 'My first name',      hint: 'your first name' },
  { token: 'me.full_name',       label: 'My full name',       hint: 'your full name' },
  { token: 'date.today',         label: "Today's date",       hint: format(new Date(), 'd MMMM yyyy') },
  { token: 'date.tomorrow',      label: "Tomorrow's date",    hint: format(addDays(new Date(), 1), 'd MMMM yyyy') },
];

const KNOWN_TOKENS = new Set(TEMPLATE_VARIABLES.map((v) => v.token));

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_.]+)\s*(?:\|([^}]*))?\}\}/g;

function clean(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function resolve(token: string, ctx: TemplateContext): string {
  switch (token) {
    case 'contact.first_name': return clean(ctx.contactFirstName);
    case 'contact.last_name':  return clean(ctx.contactLastName);
    case 'contact.full_name':  return `${clean(ctx.contactFirstName)} ${clean(ctx.contactLastName)}`.trim();
    case 'contact.email':      return clean(ctx.contactEmail);
    case 'company.name':       return clean(ctx.companyName);
    case 'ticket.number':      return clean(ctx.ticketNumber);
    case 'ticket.title':       return clean(ctx.ticketTitle);
    case 'ticket.id':          return ctx.ticketId != null ? String(ctx.ticketId) : '';
    case 'me.first_name':      return clean(ctx.myFirstName);
    case 'me.full_name':       return clean(ctx.myFullName);
    case 'date.today':         return format(new Date(), 'd MMMM yyyy');
    case 'date.tomorrow':      return format(addDays(new Date(), 1), 'd MMMM yyyy');
    default:                   return '';
  }
}

export interface AppliedTemplate {
  text: string;
  /** Tokens that resolved to nothing and had no fallback — left in place, visible to the engineer. */
  unresolved: string[];
}

/**
 * Expand a template body against a ticket's context.
 *
 * A token with no value and no fallback is deliberately left as `{{token}}` rather than
 * silently blanked, so nobody sends "Hi ," to a customer without noticing.
 */
export function applyTemplate(body: string, ctx: TemplateContext): AppliedTemplate {
  const unresolved: string[] = [];
  const text = body.replace(PLACEHOLDER_RE, (match, rawToken: string, fallback?: string) => {
    const token = rawToken.trim();
    if (!KNOWN_TOKENS.has(token)) return match;  // not ours — leave it alone
    const value = resolve(token, ctx);
    if (value) return value;
    if (fallback != null) return fallback.trim();
    if (!unresolved.includes(token)) unresolved.push(token);
    return match;
  });
  return { text, unresolved };
}

/** Placeholders present in a body, for the "uses: …" hint in the template list. */
export function templateTokens(body: string): string[] {
  const found: string[] = [];
  for (const [, rawToken] of body.matchAll(PLACEHOLDER_RE)) {
    const token = rawToken.trim();
    if (KNOWN_TOKENS.has(token) && !found.includes(token)) found.push(token);
  }
  return found;
}
